import React, { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

export const TAX_RULE_APPLICABLE_ON = [
  { value: 'room_rate', label: 'Room Rate', color: '#4ce1b6', icon: HotelOutlinedIcon },
  { value: 'hall_rate', label: 'Hall Rate', color: '#8b7cf6', icon: MeetingRoomOutlinedIcon },
  { value: 'food_beverage', label: 'Food & Beverage', color: '#f59e0b', icon: RestaurantOutlinedIcon },
  { value: 'services', label: 'Services', color: '#38bdf8', icon: RoomServiceOutlinedIcon },
  { value: 'total_amount', label: 'Total Amount', color: '#f472b6', icon: PaymentsOutlinedIcon },
  { value: 'other', label: 'Other', color: '#94a3b8', icon: CategoryOutlinedIcon },
];

const TAX_TYPE_SHORT = {
  gst: 'GST',
  cgst: 'CGST',
  sgst: 'SGST',
  vat: 'VAT',
  service_tax: 'Service Tax',
  luxury_tax: 'Luxury Tax',
  city_tax: 'City Tax',
  tourism_tax: 'Tourism Tax',
  other: 'Other',
};

function formatMoney(value, symbol = '₹') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  const formatted = amount.toLocaleString('en-IN', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
}

function formatTaxValue(rule, symbol) {
  if (rule.tax_percentage != null && rule.tax_percentage !== '') {
    const pct = Number(rule.tax_percentage);
    return Number.isFinite(pct) ? `${pct}%` : `${rule.tax_percentage}%`;
  }
  if (rule.tax_amount != null && rule.tax_amount !== '') {
    return formatMoney(rule.tax_amount, symbol);
  }
  return '—';
}

function formatAmountRange(rule, symbol) {
  const hasMin = rule.min_amount != null && rule.min_amount !== '';
  const hasMax = rule.max_amount != null && rule.max_amount !== '';
  if (!hasMin && !hasMax) return 'All amounts';
  const min = hasMin ? formatMoney(rule.min_amount, symbol) : `${symbol}0`;
  const max = hasMax ? formatMoney(rule.max_amount, symbol) : '∞';
  return `${min} – ${max}`;
}

function getTypeLabel(rule) {
  return TAX_TYPE_SHORT[rule.tax_type] || rule.tax_type_display || rule.tax_type || 'Tax';
}

function TaxMeta({ label, value, isDark }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: isDark ? alpha('#fff', 0.45) : alpha('#0f172a', 0.45),
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.82rem',
          fontWeight: 700,
          color: isDark ? '#e2e8f0' : '#0f172a',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TaxRuleCard({ rule, accent, isDark, currencySymbol, onEdit, onDelete }) {
  const typeLabel = getTypeLabel(rule);
  const taxValue = formatTaxValue(rule, currencySymbol);
  const rangeValue = formatAmountRange(rule, currencySymbol);

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        p: 1.75,
        borderRadius: 2.5,
        position: 'relative',
        overflow: 'hidden',
        background: isDark
          ? `linear-gradient(160deg, ${alpha(accent, 0.16)} 0%, ${alpha('#0b1220', 0.92)} 42%, ${alpha('#0b1220', 0.98)} 100%)`
          : `linear-gradient(160deg, ${alpha(accent, 0.14)} 0%, #ffffff 38%, #f8fafc 100%)`,
        border: `1px solid ${isDark ? alpha(accent, 0.28) : alpha(accent, 0.22)}`,
        boxShadow: isDark
          ? `0 10px 28px ${alpha('#000', 0.28)}`
          : `0 10px 24px ${alpha(accent, 0.1)}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: alpha(accent, isDark ? 0.5 : 0.4),
          boxShadow: isDark
            ? `0 16px 32px ${alpha('#000', 0.36)}`
            : `0 16px 32px ${alpha(accent, 0.16)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 5,
          bottom: 0,
          background: `linear-gradient(180deg, ${accent} 0%, ${alpha(accent, 0.45)} 100%)`,
        },
      }}
    >
      <Stack spacing={1.4} sx={{ pl: 0.75 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                label={typeLabel}
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: '0.04em',
                  color: isDark ? accent : '#0f172a',
                  backgroundColor: alpha(accent, isDark ? 0.18 : 0.2),
                  border: `1px solid ${alpha(accent, 0.35)}`,
                }}
              />
              <Chip
                size="small"
                label={rule.is_active ? 'Active' : 'Inactive'}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  color: rule.is_active
                    ? (isDark ? '#86efac' : '#166534')
                    : (isDark ? alpha('#fff', 0.62) : '#64748b'),
                  backgroundColor: rule.is_active
                    ? alpha('#22c55e', isDark ? 0.18 : 0.12)
                    : (isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.06)),
                }}
              />
            </Stack>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1rem',
                lineHeight: 1.25,
                color: isDark ? '#f8fafc' : '#0f172a',
              }}
            >
              {rule.name}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.15rem',
              letterSpacing: '-0.02em',
              color: accent,
              flexShrink: 0,
            }}
          >
            {taxValue}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            p: 1.1,
            borderRadius: 2,
            backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.03),
            border: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.05)}`,
          }}
        >
          <TaxMeta label="Type" value={rule.tax_type_display || typeLabel} isDark={isDark} />
          <TaxMeta label="Tax" value={taxValue} isDark={isDark} />
          <TaxMeta label="Amount range" value={rangeValue} isDark={isDark} />
          <TaxMeta
            label="Status"
            value={rule.is_active ? 'Active' : 'Inactive'}
            isDark={isDark}
          />
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              color: isDark ? alpha('#fff', 0.42) : alpha('#0f172a', 0.42),
            }}
          >
            {rule.currency || 'INR'}
          </Typography>
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Edit tax rule">
              <IconButton
                size="small"
                onClick={() => onEdit(rule)}
                sx={{
                  color: accent,
                  '&:hover': { backgroundColor: alpha(accent, 0.12) },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete tax rule">
              <IconButton
                size="small"
                onClick={() => onDelete(rule)}
                sx={{
                  color: '#f43f5e',
                  '&:hover': { backgroundColor: alpha('#f43f5e', 0.1) },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function FinanceTaxRulesBoard({
  taxRules = [],
  currencyOptions = [],
  isDark = false,
  onEdit,
  onDelete,
  onAdd,
}) {
  const currencySymbols = useMemo(() => {
    const map = {};
    currencyOptions.forEach((option) => {
      map[option.code] = option.symbol;
    });
    return map;
  }, [currencyOptions]);

  const groupedRules = useMemo(() => {
    const groups = Object.fromEntries(TAX_RULE_APPLICABLE_ON.map((item) => [item.value, []]));
    taxRules.forEach((rule) => {
      const key = groups[rule.applicable_on] ? rule.applicable_on : 'other';
      groups[key].push(rule);
    });
    Object.values(groups).forEach((rules) => {
      rules.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.name || '').localeCompare(String(b.name || '')));
    });
    return groups;
  }, [taxRules]);

  const visibleGroups = TAX_RULE_APPLICABLE_ON.filter(
    (group) => (groupedRules[group.value] || []).length > 0
  );

  if (visibleGroups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
        No tax rules found. Click “Add Tax Rule” to create one.
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      {visibleGroups.map((group) => {
        const Icon = group.icon;
        const rules = groupedRules[group.value] || [];
        const activeCount = rules.filter((rule) => rule.is_active).length;

        return (
          <Box key={group.value}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={1.25}
              sx={{ mb: 1.5 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${alpha(group.color, 0.22)} 0%, ${alpha(group.color, 0.08)} 100%)`,
                    border: `1px solid ${alpha(group.color, 0.28)}`,
                    color: group.color,
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.98rem',
                      color: isDark ? '#f8fafc' : '#0f172a',
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: isDark ? alpha('#fff', 0.5) : alpha('#0f172a', 0.5),
                    }}
                  >
                    {`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'} · ${activeCount} active`}
                  </Typography>
                </Box>
              </Stack>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAdd(group.value)}
                sx={{
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 1.5,
                  color: isDark ? group.color : '#0f172a',
                  backgroundColor: alpha(group.color, isDark ? 0.12 : 0.14),
                  '&:hover': {
                    backgroundColor: alpha(group.color, isDark ? 0.2 : 0.22),
                  },
                }}
              >
                Add
              </Button>
            </Stack>

            <Grid container spacing={1.5}>
              {rules.map((rule) => (
                <Grid item xs={12} sm={6} lg={4} key={rule.id}>
                  <TaxRuleCard
                    rule={rule}
                    accent={group.color}
                    isDark={isDark}
                    currencySymbol={currencySymbols[rule.currency] || '₹'}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );
}
